import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Dictionary } from '@ngrx/entity';
import { GoodSupplier } from 'entities/good-supplier';
import { OrderConfirmationItem, SupplierCheckState } from 'entities/index';
import { PurchaseOrderDeliveryType } from 'entities/purchase-order';
import { HotFlagRadioButtonType } from 'entities/purchase-order/hot-flag';
import { OpeningFrom, RequestOrderItem } from 'store/back-counter/back-counter.entities';
import { ProcurementGroupState, PurchaseOrderCreateModalComponentStore } from './purchase-order-create-modal.component.store';

@Component({
    templateUrl: './purchase-order-create-modal.component.html',
    styleUrls: ['./purchase-order-create-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PurchaseOrderCreateModalComponentStore],
    standalone: false
})
export class PurchaseOrderCreateModalComponent implements OnInit {
  componentStore = inject(PurchaseOrderCreateModalComponentStore);
  activeModal = inject(NgbActiveModal);
  hasUnassignedItem = false;

  @Input() orderNumber: string;
  @Input() sourceOrderNo: string;
  @Input() branchCode: string;
  @Input() isOnlyHotFlagCreatePO: boolean;
  @Input() openingFrom: OpeningFrom;
  @Input() requestOrderItems: RequestOrderItem[];

  public busy$ = this.componentStore.busy$;
  public groups$ = this.componentStore.groups$;

  supplierAddressDictionary: Dictionary<any> = {};
  itemIds: string[] = [];
  supplierChecks: {supplier: string, checked: boolean}[] = [];

  ngOnInit() {
    if(this.openingFrom === 'BCT') {
      this.componentStore.getProcurementGroupsByRequestOrderItem({
        orderNumber: this.orderNumber,
        sourceOrderNo: this.sourceOrderNo,
        branchCode: this.branchCode,
        requestOrderItems: this.requestOrderItems
      });
    } else {
      this.componentStore.getProcurementGroups({orderNumber: this.orderNumber, branchCode: this.branchCode});
    }
  }

  showMore(index: number) {
    this.componentStore.showMore(index);
  }

  renderAddressDropdown(deliveryType, procurementType) {
    if (deliveryType !== 'pickup') {
      return false;
    }
    switch (procurementType) {
      case 'buyout':
      case 'hotflag':        
        return true;
      default:
        return false;
    }
  }

  disablePickupAddressOptions(goodSuppliers: GoodSupplier[]) {
    return goodSuppliers.length === 1;
  }

  requirePickupAddressSelection(goodSuppliers: GoodSupplier[]) {
    return goodSuppliers.length > 1;
  }

  getGoodSupplierAddressString(goodSupplier: GoodSupplier) {
    return `${goodSupplier.streetAddress}, ${goodSupplier.city}, ${goodSupplier.state} ${goodSupplier.zipCode}`;
  }

  showLess(index: number) {
    this.componentStore.showLess(index);
  }

  updateDeliveryType(index: number, deliveryType: PurchaseOrderDeliveryType, goodSuppliers: GoodSupplier[]) {
    this.setDefaultSupplier(deliveryType, goodSuppliers, index);
    this.componentStore.updateDeliveryType({ key: index, value: deliveryType });
  }

  updateSameAddress(index: number, sameAddress: boolean) {
    this.componentStore.updateSameAddress({ key: index, value: sameAddress });
  }

  setDefaultSupplier(deliveryType: PurchaseOrderDeliveryType, goodSuppliers: GoodSupplier[], index: number) {
    let addressIndex = deliveryType === 'pickup' && goodSuppliers?.length === 1 ? 0 : null;
    let defaultSupplier = null;
    if (addressIndex !== null) {
      defaultSupplier = goodSuppliers[addressIndex].supplier;
    }
    this.updateSupplier(index, defaultSupplier);
  }

  updateSupplier(index: number, supplier: string) {
    this.componentStore.updateSupplier({ key: index, value: supplier });
  }

  updateHotFlagItems(index: number, hotFlagType: HotFlagRadioButtonType) {
    this.componentStore.updateHotFlagItems({ key: index, value: hotFlagType });
  }

  cancel() {
    this.activeModal.dismiss();
  }

  submit() {
    this.componentStore.submit();
  }

  selectAllChecked(event, supplier?: string, orderItems?: (OrderConfirmationItem & SupplierCheckState)[]) {
    if(this.openingFrom === 'BCT') {
      this.itemIds = [...this.itemIds, ...orderItems.map(m => m._id)];
    } else {
      this.itemIds = [...this.itemIds, ...orderItems.map(m => m.cartItemId)];
    }
    this.componentStore.selectAll({checked: event.target.checked, supplier});
    const suppliers = this.getSuppliersByOrderItems(orderItems);
    this.supplierChecks = [...suppliers].map(m => {
      if(m === supplier) {
        return {supplier: m, checked: event.target.checked};
      }
      return { supplier: m, checked: false}; 
    })
  }

  setSupplier(vendorNumber: string, orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    if(this.openingFrom === 'BCT') {
      this.supplierAddressDictionary[vendorNumber] = orderItems.filter(f => this.itemIds.includes(f._id));
      const filteredOrderItems = orderItems.filter(f => this.itemIds.includes(f._id));
      this.componentStore.setSupplier({vendorNumber, filteredOrderItems});
    } else {
      this.supplierAddressDictionary[vendorNumber] = orderItems.filter(f => this.itemIds.includes(f.cartItemId));
      const filteredOrderItems = orderItems.filter(f => this.itemIds.includes(f.cartItemId));
      this.componentStore.setSupplier({vendorNumber, filteredOrderItems});
    }
    this.componentStore.resetCheckItems();
    this.resetItemIdAndDictonaryData();
  }

  getSupplierName(supplier: string, goodSuppliers: GoodSupplier[]) {
    return this.getGoodSupplierAddressString(goodSuppliers.find(f => f.supplier === supplier))
  }

  renderVendors(goodSuppliers: GoodSupplier[], orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    if(goodSuppliers?.length > 0) {
      return true;
    }
    return false;
  }

  hasUnassignedItems(orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    return !orderItems.every(e => e.supplier);
  }

  noSupplierOrderItems(orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    return orderItems.filter(f => !f.supplier);
  }

  orderItemChecked(item: OrderConfirmationItem & SupplierCheckState) {
    if(this.openingFrom === 'BCT' && item.checked) {
      this.itemIds.push(item._id);
    }
    else if(this.openingFrom === 'BCT' && !item.checked) {
      this.itemIds = this.itemIds.filter(f => item._id !== f);
    }
    else if(this.openingFrom !== 'BCT' && item.checked) {
      this.itemIds.push(item.cartItemId);
    }
    else {
      this.itemIds = this.itemIds.filter(f => item.cartItemId !== f);
    }
  }

  selectAllUnassignedChecked(event: any, orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    const checked = event.target.checked;
    const unassignedItem = this.noSupplierOrderItems(orderItems);
    this.componentStore.selectAllUnassigned({checked, unassignedItem});
  }

  setUnassignedSupplier(vendorNumber: string, orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    const checkedItems = orderItems.filter(f => f.checked && !f.supplier);
    this.componentStore.setCheckedItemsWithSupplier({vendorNumber, checkedItems});
    this.resetItemIdAndDictonaryData();
  }

  getSuppliersByOrderItems(orderItems: (OrderConfirmationItem & SupplierCheckState)[]) {
    return new Set([...orderItems.filter(f => f.supplier)].map(f => f.supplier));
  }

  getOrderItemsBySupplier(orderItems: (OrderConfirmationItem & SupplierCheckState)[], supplierItem: string) {
    return orderItems.filter(f => supplierItem === f.supplier);
  }

  resetItemIdAndDictonaryData() {
    this.itemIds = [];
    this.supplierAddressDictionary = {};
    this.supplierChecks = [];
  }

  hasUnassignedItemByGroups(groups: ProcurementGroupState[]) {
    for(let i of groups) {
      if(this.hasUnassignedItems(i.orderItems) && i.state.sameAddress === false) {
        return true;
      }
    }
    return false;
  }

  hasHiddenItems(groups: ProcurementGroupState[])  {
    return !groups.every(s => !s.hasMore || (s.hasMore && s.state.showMore));
  }

  isCheckedSupplierItem(supplier: string) {
    return this.supplierChecks.find(f => f.supplier === supplier)?.checked;
  }

  isSubmitDisabled(formValid: boolean, groups: ProcurementGroupState[]) {
    return !formValid || this.hasUnassignedItemByGroups(groups) || this.hasHiddenItems(groups);
  }
}
